import { BeakerIcon, PlayIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Button } from '@/components/core/button';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import type {
  CreateSimulationScenarioDTO,
  SimulationDTO,
  SimulationScenarioDTO,
} from '@/gateways/impl/SimulationGateway';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { isTerminalRunStatus } from '../shared/inputs-status';
import { ScenarioConfigSection } from './scenario-config-section';
import { ScenarioResultsPlaceholder } from './scenario-results-placeholder';

type SimulationDetailsData = {
  id: string;
  simulation: SimulationDTO;
  scenarios: SimulationScenarioDTO[];
};

export function ScenarioDetails() {
  const { id: simulationId, scenarioId } = useParams<{ id: string; scenarioId: string }>();
  const navigate = useNavigate();
  const messages = useMessages();
  const { simulationGateway } = useGateways();

  const { data, isLoading } = useEntityDetailsQuery<SimulationDetailsData>({
    parentKey: 'simulations',
    id: simulationId,
    isNew: !simulationId,
    query: async (fetchId, { signal }) => {
      const simulationDetails = await simulationGateway.getSimulation(fetchId, { signal });
      return { id: simulationDetails.simulation.id, ...simulationDetails };
    },
    refetchInterval: ({ state }) => {
      if (!state.data) return 2000;
      const current = state.data.scenarios.find((s) => s.id === scenarioId);
      if (!current) return false;
      return isTerminalRunStatus(current.runStatus) ? false : 2000;
    },
  });

  const simulation = data?.simulation;
  const scenario = useMemo(
    () => data?.scenarios.find((s) => s.id === scenarioId),
    [data, scenarioId],
  );

  const { mutate: rerunMutate } = useGatewayMutation({
    mutationFn: (input: CreateSimulationScenarioDTO) =>
      simulationGateway.createSimulationScenario(simulationId!, input),
    linkedQuery: ['simulations', simulationId],
    linkResponseToQuery: false,
    onSuccess: () => {
      messages.success('Scenario re-run started');
      void navigate(`/simulations/${simulationId}`);
    },
  });

  const onRerun = () => {
    if (!scenario) return;
    const { configSnapshot } = scenario;
    void rerunMutate({
      name: scenario.name ? `Re-run of ${scenario.name}` : null,
      accountId: configSnapshot.accountId,
      locationId: configSnapshot.locationId,
      carrierServiceIds: configSnapshot.carrierServiceIds,
    });
  };

  const title = scenario?.name || `Scenario ${scenarioId?.slice(0, 8) ?? ''}`;

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon
          parentUrl={`/simulations/${simulationId}`}
          parentTitle={simulation?.name ?? 'Simulation'}
        >
          <BeakerIcon />
        </HeaderIcon>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderActions>
          <Button variant="outline" onClick={onRerun} disabled={!scenario}>
            <PlayIcon aria-hidden />
            Re-run
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <ScenarioConfigSection scenario={scenario} isLoading={isLoading} />
          <ScenarioResultsPlaceholder scenario={scenario} isLoading={isLoading} />
        </DetailMain>
      </PageContent>
    </Page>
  );
}
